import axios from 'axios';
import { logout } from '../services/auth/login';
import { apiBaseUrl } from './apiBaseUrl';
import { tokenStorage } from './tokenStorage';
import {
  isUnreachableError,
  markBackendUp,
  reportApiFailure,
} from './backendStatus';

const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
});

const refreshTokenInstance = axios.create({
  baseURL: apiBaseUrl,
});

/**
 * Token refresh queue management.
 *
 * When multiple requests fail simultaneously due to an expired token,
 * only one refresh request is sent. Other requests wait in a queue
 * and are retried once the new token is available.
 */
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

/**
 * Notifies all queued requests that a new token is available.
 * Executes their callbacks with the new token and clears the queue.
 */
const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

/**
 * Adds a request callback to the queue while waiting for token refresh.
 */
const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Request interceptor: attaches access token to outgoing requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handles 403 errors and refreshes token.
// It doubles as the passive liveness signal feeding the maintenance screen: every answered
// request marks the API up, every unreachable one is reported so the outage can be confirmed.
axiosInstance.interceptors.response.use(
  (response) => {
    markBackendUp();
    return response;
  },
  async (error) => {
    void reportApiFailure(error);
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 403 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        logout();
        return Promise.reject(error);
      }

      // If a refresh is already in progress, wait for the new token
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const response = await refreshTokenInstance.post(
          'users/token/refresh-token',
          {
            refreshToken: refreshToken,
          },
        );

        if (response.status === 200) {
          const newAccessToken = response.data.accessToken;
          const newRefreshToken = response.data.refreshToken;

          tokenStorage.updateTokens(newAccessToken, newRefreshToken);

          isRefreshing = false;
          onRefreshed(newAccessToken);

          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshTokenError) {
        isRefreshing = false;
        refreshSubscribers = [];
        // A refresh that failed because the API is unreachable (deploy, gateway restart) says
        // nothing about the session: logging the user out there would turn a few minutes of
        // downtime into a forced re-login for everyone.
        if (!isUnreachableError(refreshTokenError)) {
          logout();
        }
        return Promise.reject(refreshTokenError);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
